import { defineStore, acceptHMRUpdate } from 'pinia'
import { useMiscStore } from './misc'
import { fb } from '@/lib/api/firebase'
import { API } from '@/lib/api'
import { groupByKey } from '@/lib/utils'
import { signInAnonymously, signInWithEmailAndPassword, onAuthStateChanged, getIdTokenResult, signOut, sendPasswordResetEmail, signInWithCustomToken } from 'firebase/auth'

export const useUserStore = defineStore('user', {
	state: () => ({
		me: null,
		claims: {},
		authData: {},
		users: [],
		user: {},
		token: null,
		_loading: 0,
		error: null,
	}),

	actions: {
		async _setLoading(status, global = true) {
			if (status) {
				this.error = null
				this._loading = Math.max(this._loading + 1, 0)
			} else {
				this._loading = Math.max(this._loading - 1, 0)
			}

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setLoading(status)
			}
		},

		async _setError(error = null, global = true) {
			this.error = error
			console.group(`OrganizationStore`)
			console.warn(error)
			console.groupEnd()

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setError(error)
			}
		},

		signInAnonymously() {
			signInAnonymously(fb.auth)
			onAuthStateChanged(fb.auth, (user) => {
				console.log('onAuthStateChanged', user)
				this.me = user
			})
		},

		checkUser() {
			return new Promise((resolve, reject) => {
				onAuthStateChanged(fb.auth, async (user) => {
					console.log('store.checkUser', {user})
					this.me = user
					if (user) {
						try {
							const tokenData = await getIdTokenResult(user)
console.log('[checkUser]', { tokenData, })
							const { claims } = tokenData
							this.claims = claims
							this.token = tokenData
						} catch (error) {
							reject(error)
						}
						resolve(user)
					} else {
						reject(`No user`)
					}
				}, reject)
			})
		},

		async loginRemote(fields) {
			const { email, password } = fields
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/admin/authenticate`, { email, password }, {}, false)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res
						} else {
							this.authData = {}
						}

console.log('[loginRemote]', {auth})
						const { user } = auth
						resolve(user)
					}
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async loginVerify(fields) {
			const { code, otpSecret } = fields

			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/admin/verify`, { code, otpSecret })
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res
						} else {
							this.authData = null
						}

						console.log('[loginVerify]', { auth, })
						const { user } = auth
						resolve(user)
					}
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async authSelect(fields) {
			this._setLoading(true)

			const { token, ...data } = fields

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/admin/select`, data)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res.data
						} else {
							this.authData = null
						}

						const { user } = auth
						resolve(user)
					}
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async signInCustom(token) {
			const auth = await signInWithCustomToken(fb.auth, token)
			await this.checkUser()
			const { user } = auth
			return user
		},

		async registerInvite(fields) {
			const { code, email, firstName, lastName, password,  } = fields
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {

				try {
					const res = await API.post(`/users/invite`, { code, email, firstName, lastName, password }, {}, false)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						const { user } = auth
						this.me = user

						resolve(user)
					}
				} catch (error) {
					reject(error)
					// this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async register(fields) {
			const { code, email, firstName, lastName, password,  } = fields
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/users/register`, { email, firstName, lastName, password }, {}, false)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						const { user } = auth
						this.me = user
	console.log('login', user)
						resolve(user)
					} else {
						reject(`No user`)
					}
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async reauth(customerId) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {

				try {
					const res = await API.post(`/users/reauth`, { customerId })
					console.log({ res, })
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						const { user } = auth
						this.me = user
	console.log('login', user)
						resolve(user)
					}
				} catch (error) {
					reject(error)
					this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async getProducts() {

			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.get(`/users/products`, null, {}, false)
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async getProduct(productId) {

			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.get(`/users/product/${productId}`, null, {}, false)
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async loginUser(fields) {
			const { email, password } = fields
			const miscStore = useMiscStore()
			miscStore.setLoading(true)
			miscStore.setError(null)

			return new Promise(async (resolve, reject) => {
				try {
					const auth = await signInWithEmailAndPassword(fb.auth, email, password)
					const { user } = auth
					this.me = user

					let docPath = [`users`, user.uid].join(`/`)
					try {
						const {uid, displayName, email, } = user
						await fb.saveDoc(docPath, { displayName, email, uid })
					} catch (error) {
						console.error(error)
					}

					resolve(this.me)
				} catch (error) {
					reject(error)
					miscStore.setError(error)
				} finally {
					miscStore.setLoading(false)
				}
			})
		},

		async signOut() {
			const miscStore = useMiscStore()
			miscStore.setLoading(true)
			miscStore.setError(null)

			await signOut(fb.auth)
			this.me = null
			this.authData = {}
			this.claims = {}
			miscStore.setLoading(false)
		},

		async resetPassword(fields) {
			const miscStore = useMiscStore()
			miscStore.setLoading(true)
			miscStore.setError(null)

			const { email } = fields
			return new Promise(async (resolve, reject) => {
				try {
					await sendPasswordResetEmail(fb.auth, email)
					resolve(email)
				} catch (error) {
					reject(error)
					miscStore.setError(error)
				} finally {
					miscStore.setLoading(false)
				}

			})
		},

		async getUsers() {
			this._setLoading(true)
			return new Promise(async (resolve, reject) => {
				try {
					this.users = await API.get(`/users`)
					resolve(this.users)
				} catch (error) {
					reject(error)
					this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async getUser(userId) {
			const miscStore = useMiscStore()
			miscStore.setLoading(true)
			miscStore.setError(null)

			return new Promise(async (resolve, reject) => {
				try {
					this.user = await API.get(`/users/${userId}`)
					resolve(this.user)
				} catch (error) {
					miscStore.setError(error)
					reject(error)
				} finally {
					miscStore.setLoading(false)
				}
			})

		},

		async upgradeUser(uid) {
			const miscStore = useMiscStore()
			miscStore.setLoading(true)
			miscStore.setError(null)

			let docsPath = [`users`].join(`/`)

			try {
				await API.post(`/users/${uid}/upgrade`)
				// this.users = await fb.getDocs(docsPath)
			} catch (error) {
				miscStore.setError(error)
			} finally {
				miscStore.setLoading(false)
			}
		},

		async updateUser(uid, data) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const { uid, [`@odata.etag`]: etag, customers,  ...updateData} = data
					const headers = {
						'If-Match': etag
					}
					const user = await API.patch(`/users/${uid}`, updateData, {}, true, headers)
					this.user = {...user}
					this.users = this.users.map(a => a.uid == uid ? user : a)
					resolve(user)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		unsetUser() {
			this.user = {}
		},

		unsetUsers() {
			this.users = []
		},

		async checkout(uid, params) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {

				try {
					const session = await API.post(`/users/checkout`, params)
					console.log({ session,})
					resolve(session)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})

		}
	},

	getters: {
		isAdmin: (state) => state.me?.email?.match(/@(chaselawfloridapa|5inline)\.com/)?.length,
		userLevel: (state) => state.token?.claims?.metadata?.userLevel,
		isLoading: state => state._loading > 0,
	}
})
